// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useState } from 'react';
import { Modal } from '@loa/ui-components';
import { calculationResultApi } from '../../../../api/calculationResultApi';

type MaxFundingDeleteScenarioModalProps = Readonly<{
  open: boolean;
  profileId: string;
  scenarioId: string;
  onClose: () => void;
  onSuccess: () => void;
}>;

export function MaxFundingDeleteScenarioModal({
  open,
  profileId,
  scenarioId,
  onClose,
  onSuccess,
}: MaxFundingDeleteScenarioModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    try {
      setLoading(true);
      setError(null);
      await calculationResultApi.remove(profileId, scenarioId);
      onSuccess();
    } catch (err) {
      const apiError = err as { response?: { data?: { message?: string } }; message?: string };
      setError(
        apiError.response?.data?.message ||
          apiError.message ||
          'Failed to delete scenario. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setError(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} size="small" variant="warning">
      <Modal.Header icon="trash" title="Delete Scenario" onClose={handleClose} />
      <Modal.Content>
        <div
          className="text-[#000] font-sans text-body-01 font-normal leading-normal"
          data-testid="delete-scenario-modal"
        >
          Are you sure you want to delete this scenario?
          {error && (
            <div
              className="mt-md p-[12px] bg-[#FFF2F3] border border-error-text rounded-lg text-error-text text-body-03"
              role="alert"
            >
              {error}
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: handleClose, disabled: loading, colour: 'red' }}
        primaryAction={{
          label: 'Delete Scenario',
          onClick: () => { void handleDelete(); },
          loading,
          disabled: loading,
          colour: 'red',
        }}
      />
    </Modal>
  );
}
